import React, { Dispatch, RefObject, useMemo } from 'react';
import { Checkbox, SxProps, TableCell, TableHead, TableRow } from '@mui/material';
import { classes } from './styles';
import { handleSelectAllOnPage } from './tableHelper';
import {
  GenericTableData,
  TPaginationState,
  TSortableColumn,
  TTableState,
  TablePaginationAction,
  TableSelectionAction
} from './types';
import { updateUiSettings } from '../../helpers/uiPreferencesHelpers';
import TooltipTableCell from './TooltipTableCell';

interface IGenericTableHeadProps<T> {
  tableState: TTableState<T, TSortableColumn[]>;
  paginationState: TPaginationState<string>;
  dispatch: Dispatch<TableSelectionAction<T> | TablePaginationAction<string>>;
  items: T[];
  titleCellRef: RefObject<HTMLDivElement>;
  actionCellRef: RefObject<HTMLDivElement>;
  headerStyle: (id: string, tableHovered: boolean) => SxProps;
  titleCellId?: string;
  userPreferenceSource?: string;
  hasActionItems?: boolean;
  allowMultipleSelection?: boolean;
  customRowStyles?: Record<string, SxProps>;
}

const GenericTableHead = <GenericTableHeadProps extends GenericTableData>({
  tableState,
  paginationState,
  dispatch,
  items,
  titleCellRef,
  actionCellRef,
  headerStyle,
  titleCellId = 'name', // This is used to attach a ref to the title cell for the table arrows
  userPreferenceSource,
  hasActionItems = true,
  allowMultipleSelection = true,
  customRowStyles
}: IGenericTableHeadProps<GenericTableHeadProps>) => {
  const selectAllIsIndeterminate = useMemo(
    () => items.map((item) => item.id).some((id) => tableState.selectedItemIds.includes(id)),
    [items, tableState.selectedItemIds]
  );
  const selectAllIsChecked = useMemo(
    () => items.map((item) => item.id).every((id) => tableState.selectedItemIds.includes(id)),
    [items, tableState.selectedItemIds]
  );

  const handleSortChange = (id: string) => {
    const sortOrder =
      paginationState.sort === id && paginationState.sort_order === 'asc' ? 'desc' : 'asc';
    dispatch({ type: 'SET_SORT', payload: { sortBy: id, sortOrder } });
    if (userPreferenceSource) {
      updateUiSettings({
        source: userPreferenceSource,
        sorting: {
          sortBy: id,
          sortOrder
        }
      });
    }
  };

  return (
    <TableHead>
      <TableRow sx={customRowStyles}>
        {allowMultipleSelection && (
          <TableCell sx={headerStyle('checkbox', tableState.tableHovered)}>
            <Checkbox
              id="select-all-checkbox"
              sx={classes.checkbox}
              indeterminate={selectAllIsIndeterminate && !selectAllIsChecked}
              checked={selectAllIsChecked}
              onChange={(e) => handleSelectAllOnPage(e, items, dispatch)}
              inputProps={{
                'aria-label': 'select all items on page'
              }}
            />
          </TableCell>
        )}
        {tableState.columns.map((header) => {
          const baseStyles = headerStyle(header.id, tableState.tableHovered);
          const isSticky =
            allowMultipleSelection &&
            baseStyles &&
            baseStyles.position === 'sticky' &&
            baseStyles.left;
          let left = undefined;
          if (isSticky) {
            left =
              typeof baseStyles.left === 'number'
                ? baseStyles.left + 58
                : `calc(${baseStyles.left} + 58px)`;
          }
          const adjustedStyles = { ...baseStyles, left };

          return (
            <TooltipTableCell
              key={header.id}
              header={header}
              paginationState={paginationState}
              onSortChange={handleSortChange}
              ref={header.id === titleCellId ? titleCellRef : undefined}
              sx={adjustedStyles}
            />
          );
        })}
        <TableCell
          sx={{
            ...headerStyle('actions', tableState.tableHovered),
            ...(!hasActionItems && { minWidth: '20px', width: '20px' })
          }}
          ref={actionCellRef}
        />
      </TableRow>
    </TableHead>
  );
};

export default GenericTableHead;
